import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  Info,
  Trash,
} from 'lucide-react'
import React, { useContext, useState } from 'react'
import { toast } from 'sonner'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import AnimateHeightAndOpacity from 'components/reusable/AnimateHeightAndOpacity'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { Badge, Button, FileList, FileUpload, IconButton } from 'slab'
import {
  deleteBrokersMembersMemberIdFileUploadsBlobId,
  getGetBrokersMembersMemberIdQueryKey,
  useGetBrokersMembersMemberId,
} from 'utils/web_api'
import {
  type BrokersDocument,
  type DeleteBrokersMembersMemberIdFileUploadsBlobIdParams,
  DeleteBrokersMembersMemberIdFileUploadsBlobIdUploadType
} from 'utils/web_api.schemas'

import { CollapsibleDataContainer } from '../reusable/CollapsibleDataContainer'
import { SkeletonItem } from '../reusable/SkeletonItem'

interface BrokersMembersUploadsViewProps {
  memberId: string
}

interface DocumentGroup {
  title: string
  documents: BrokersDocument[]
  isUploadable?: boolean
  tooltip?: string
}

interface PendingFile {
  id: string
  filename: string
  byte_size: number
  status: 'pending' | 'uploading' | 'error'
  file: File
  isPrivate: boolean
  errorMessage?: string
}

const DocumentItem = ({
  document,
  memberId
}: {
  document: BrokersDocument
  memberId: string
}) => {
  const queryClient = useQueryClient()

  const toDeleteParamType = (
    t: BrokersDocument['document_type']
  ): DeleteBrokersMembersMemberIdFileUploadsBlobIdParams['upload_type'] => {
    if (t in DeleteBrokersMembersMemberIdFileUploadsBlobIdUploadType) {
      return t as DeleteBrokersMembersMemberIdFileUploadsBlobIdParams['upload_type']
    }

    throw new Error('Unsupported document type')
  }

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return await deleteBrokersMembersMemberIdFileUploadsBlobId(memberId, document.id, {
        upload_type: toDeleteParamType(document.document_type),
      })
    },
    onSuccess: () => {
      // Invalidate the member query to refresh the document list
      queryClient.invalidateQueries({
        queryKey: getGetBrokersMembersMemberIdQueryKey(memberId),
      })
      toast.success('File deleted successfully')
    },
    onError: (error) => {
      console.error('Delete failed:', error)
      toast.error('Failed to delete file. Please try again.')
    },
  })

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this file? This cannot be undone.')) {
      deleteMutation.mutate()
    }
  }

  return (
    <FileList.File
      key={document.id}
      name={document.filename}
      size={document.byte_size}
      status='success'
      uploadedAt={new Date(document.created_at)}
      actions={
        <div className='flex items-center space-x-2'>
          {!document.visible_to_member && <Badge type='warning'>Private</Badge>}
          <IconButton
            icon={Download}
            label='Download'
            onClick={() => window.open(document.download_url, '_blank')}
          />
          <IconButton
            icon={FileText}
            label='View'
            onClick={() => window.open(document.view_url, '_blank')}
          />
          {document.document_type in DeleteBrokersMembersMemberIdFileUploadsBlobIdUploadType && (
            <IconButton
              icon={Trash}
              label='Delete'
              variant='destructive'
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            />
          )}
        </div>
      }
    />
  )
}

const PendingFileItem = ({
  pendingFile,
  onRemove,
}: {
  pendingFile: PendingFile
  onRemove: () => void
}) => {
  const getStatusProps = () => {
    switch (pendingFile.status) {
      case 'pending':
        return { status: 'pending' as const }
      case 'uploading':
        return { status: 'uploading' as const }
      case 'error':
        return {
          status: 'error' as const,
          errorMessage: pendingFile.errorMessage,
        }
      default:
        return { status: 'pending' as const }
    }
  }

  return (
    <FileList.File
      name={pendingFile.filename}
      size={pendingFile.byte_size}
      {...getStatusProps()}
      actions={
        <div className='flex items-center space-x-2'>
          {pendingFile.isPrivate && <Badge type='warning'>Private</Badge>}
          {pendingFile.status === 'pending' && (
            <IconButton icon={Trash} label='Remove' onClick={onRemove} />
          )}
        </div>
      }
    />
  )
}

const DocumentSection = ({
  documents,
  title,
  isUploadable,
  tooltip,
}: DocumentGroup) => {
  const { memberId } = useContext(BrokersMemberContext)
  const [showUpload, setShowUpload] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])

  const queryClient = useQueryClient()

  const handleViewDetails = () => {
    setShowUpload(!showUpload)
  }

  const uploadFilesMutation = useMutation({
    mutationFn: async ({
      files,
      isPrivate,
    }: {
      files: File[]
      isPrivate: boolean
    }) => {
      const formData = new FormData()

      files.forEach((file) => {
        formData.append('files[]', file)
      })

      if (isPrivate) {
        formData.append('private', 'true')
      }

      const response = await fetch(
        `/api/web/brokers/members/${memberId}/upload_files`, // eslint-disable-line thatch/no-bare-fetch
        {
          method: 'POST',
          body: formData,
        }
      )

      if (!response.ok) {
        throw new Error('Upload failed')
      }

      return response.json()
    },
    onSuccess: () => {
      // Clear pending files on success and invalidate the member query to refresh the cache.
      setPendingFiles([])
      queryClient.invalidateQueries({
        queryKey: getGetBrokersMembersMemberIdQueryKey(memberId),
      })
    },
    onError: () => {
      // Mark all uploading files as error.
      setPendingFiles((prev) =>
        prev.map((file) =>
          file.status === 'uploading'
            ? { ...file, status: 'error', errorMessage: 'Upload failed' }
            : file
        )
      )
    },
  })

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files
    if (!fileList) return

    const files = Array.from(fileList)

    // Add files to pending state immediately
    const newPendingFiles: PendingFile[] = files.map((file) => ({
      id: `pending-${Date.now()}-${Math.random()}`,
      filename: file.name,
      byte_size: file.size,
      status: 'pending',
      file,
      isPrivate: false, // Default to public, could be made configurable
    }))

    setPendingFiles((prev) => [...prev, ...newPendingFiles])
  }

  const handleUploadFiles = (isPrivate: boolean = false) => {
    const filesToUpload = pendingFiles.filter((f) => f.status === 'pending')

    if (filesToUpload.length === 0) return

    // Mark files as uploading
    setPendingFiles((prev) =>
      prev.map((file) =>
        file.status === 'pending'
          ? { ...file, status: 'uploading', isPrivate }
          : file
      )
    )

    // Perform the upload
    uploadFilesMutation.mutate({
      files: filesToUpload.map((f) => f.file),
      isPrivate,
    })
  }

  const handleRemovePendingFile = (fileId: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.id !== fileId))
  }

  // Merge documents and pending files for display
  const allFiles = [
    ...pendingFiles.map((file) => ({ type: 'pending' as const, data: file })),
    ...documents
      .map((doc) => ({ type: 'document' as const, data: doc }))
      .sort(
        (a, b) =>
          new Date(b.data.created_at).getTime() -
          new Date(a.data.created_at).getTime()
      ),
  ]

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-start gap-2 mb-2'>
        <h3 className='text-lg font-medium text-gray-900'>{title}</h3>
        <div className='flex'>
          {tooltip && (
            <DisplayTooltipOnHover text={tooltip} tooltipClassName='w-80'>
              <Info size={16} />
            </DisplayTooltipOnHover>
          )}
          {isUploadable && (
            <Button
              onClick={handleViewDetails}
              variant='tertiary'
              rightIcon={showUpload ? ChevronDown : ChevronRight}
            >
              {showUpload ? 'Hide upload' : 'Upload files'}
            </Button>
          )}
        </div>
      </div>

      <AnimateHeightAndOpacity isOpen={showUpload}>
        {isUploadable && (
          <div className='mb-2'>
            <FileUpload
              accept='.pdf,.jpg,.jpeg,.png'
              multiple
              data-testid='file-upload'
              onChange={handleFilesSelected}
            >
              <FileUpload.Content layout='horizontal' size='small' />
            </FileUpload>
            <AnimateHeightAndOpacity isOpen={pendingFiles.length > 0}>
              {pendingFiles.length > 0 && (
                <div className='py-2 flex gap-2'>
                  <Button
                    onClick={() => handleUploadFiles(true)}
                    disabled={
                      uploadFilesMutation.isPending ||
                      pendingFiles.every((f) => f.status !== 'pending')
                    }
                    variant='primary'
                    size='small'
                  >
                    Upload as Private
                  </Button>
                  <Button
                    onClick={() => handleUploadFiles(false)}
                    disabled={
                      uploadFilesMutation.isPending ||
                      pendingFiles.every((f) => f.status !== 'pending')
                    }
                    variant='secondary'
                    size='small'
                  >
                    Upload as Public
                  </Button>
                </div>
              )}
            </AnimateHeightAndOpacity>
          </div>
        )}
      </AnimateHeightAndOpacity>

      {allFiles.length > 0
        ? (
          <FileList>
            {allFiles.map((item) =>
              item.type === 'document'
                ? (
                  <DocumentItem
                    key={item.data.id}
                    document={item.data}
                    memberId={memberId}
                  />
                  )
                : (
                  <PendingFileItem
                    key={item.data.id}
                    pendingFile={item.data}
                    onRemove={() => handleRemovePendingFile(item.data.id)}
                  />
                  )
            )}
          </FileList>
          )
        : (
          <div className='text-center py-2 text-gray-500'>No documents</div>
          )}
    </div>
  )
}

const Heading = () => (
  <div className='flex items-center space-x-2'>
    <span className='heading-md'>Member documents</span>
  </div>
)

const UploadsSkeleton = () => (
  <div className='p-4 space-y-6'>
    <div className='space-y-4'>
      <SkeletonItem widthClass='w-48' heightClass='h-6' />
      <div className='space-y-2'>
        <SkeletonItem widthClass='w-full' heightClass='h-12' />
        <SkeletonItem widthClass='w-full' heightClass='h-12' />
        <SkeletonItem widthClass='w-full' heightClass='h-12' />
      </div>
    </div>
    <div className='space-y-4'>
      <SkeletonItem widthClass='w-48' heightClass='h-6' />
      <div className='space-y-2'>
        <SkeletonItem widthClass='w-full' heightClass='h-12' />
        <SkeletonItem widthClass='w-full' heightClass='h-12' />
      </div>
    </div>
    <div className='space-y-4'>
      <SkeletonItem widthClass='w-48' heightClass='h-6' />
      <div className='space-y-2'>
        <SkeletonItem widthClass='w-full' heightClass='h-12' />
        <SkeletonItem widthClass='w-full' heightClass='h-12' />
        <SkeletonItem widthClass='w-full' heightClass='h-12' />
      </div>
    </div>
  </div>
)

export default function BrokersMembersUploadsView ({
  memberId,
}: BrokersMembersUploadsViewProps) {
  const {
    data: member,
    isLoading,
    error,
  } = useGetBrokersMembersMemberId(memberId, {
    query: {
      enabled: !!memberId,
      refetchOnWindowFocus: false,
    },
  })

  const groupDocuments = (documents: BrokersDocument[]): DocumentGroup[] => {
    const memberUploads = documents.filter(
      (doc) => doc.document_type === 'member_upload'
    )
    const thatchUploads = documents.filter(
      (doc) => doc.document_type === 'thatch_upload'
    )
    const brokerUploads = documents.filter(
      (doc) => doc.document_type === 'broker_upload'
    )
    const brokerPrivateUploads = documents.filter(
      (doc) => doc.document_type === 'broker_private_upload'
    )

    return [
      {
        title: 'Member uploaded files',
        documents: memberUploads,
      },
      {
        title: 'Thatch uploaded files for this member',
        documents: thatchUploads,
      },
      {
        title: 'Broker uploaded files for this member',
        documents: [...brokerUploads, ...brokerPrivateUploads],
        isUploadable: true,
        tooltip:
          'If you need to securely share sensitive files for this member, please upload them here. You can choose whether the files are visible to the member or kept private.',
      },
    ]
  }

  const successContent = (
    <div className='p-4 space-y-10'>
      {groupDocuments(member?.documents || []).map((group) => (
        <DocumentSection
          key={group.title}
          title={group.title}
          documents={group.documents}
          isUploadable={group.isUploadable}
          tooltip={group.tooltip}
        />
      ))}
    </div>
  )

  return (
    <CollapsibleDataContainer
      heading={<Heading />}
      isLoading={isLoading}
      error={!!error || !member}
      localStorageKey='memberUploadsOpenByDefault'
      loadingContent={<UploadsSkeleton />}
      errorContent={
        <div className='p-4 text-center text-red-500'>
          Error loading member documents
        </div>
      }
      successContent={successContent}
    />
  )
}
